// src/services/apiService.js

// ENV-based URL switching
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const WP_API_BASE_URL = process.env.REACT_APP_WORDPRESS_API_BASE_URL;
const LEAD_API_BASE_URL = process.env.REACT_APP_LEAD_API_BASE_URL;


// ----------- Helper Functions (Internal to this module) -----------

/**
 * Strip HTML tags from a string.
 */
function stripHtml(html) {
  if (!html) return "";
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || "";
}

/**
 * Count words in a text.
 */
function countWords(text) {
  if (!text) return 0;
  const plainText = stripHtml(text);
  return plainText.trim().split(/\s+/).filter(word => word.length > 0).length;
}

/**
 * Transforms a raw WordPress post object into an optimized object.
 * Requires the API response to include _embed.
 */
function transformBlogPost(post) {
  const wpBaseUrl = 'https://blogs.oolook.in';

  // Helper to make any partial URL absolute
  const makeAbsolute = (url) => {
    if (!url || url.startsWith('http')) {
      return url;
    }
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${wpBaseUrl}${path}`;
  };

  const authorData = post._embedded?.author?.[0];
  const featuredMediaData = post._embedded?.["wp:featuredmedia"]?.[0] || null;
  let categories = [], tags = [];
  if (post._embedded?.["wp:term"]) {
    post._embedded["wp:term"].forEach(termList => {
      if (termList.length > 0) {
        if (termList[0].taxonomy === "category") categories = termList.map(cat => ({ id: cat.id, name: cat.name }));
        else if (termList[0].taxonomy === "post_tag") tags = termList.map(tag => ({ id: tag.id, name: tag.name }));
      }
    });
  }

  // Robust replacement for all image paths.
  const fixContentPaths = (htmlString) => {
      if (!htmlString) return "";
      return htmlString
          .replace(/src="\/wp-content\//g, `src="${wpBaseUrl}/wp-content/`)
          .replace(/src="wp-content\//g, `src="${wpBaseUrl}/wp-content/`)
          .replace(/srcset="\/wp-content\//g, `srcset="${wpBaseUrl}/wp-content/`)
          .replace(/srcset="wp-content\//g, `srcset="${wpBaseUrl}/wp-content/`);
  };

  const absoluteContentHtml = fixContentPaths(post.content.rendered);
  const absoluteExcerptHtml = fixContentPaths(post.excerpt?.rendered);

  return {
    id: post.id,
    dateTime: post.date,
    slug: post.slug,
    title: post.title.rendered,
    content: absoluteContentHtml,
    excerpt: absoluteExcerptHtml,
    wordCount: countWords(post.content.rendered),
    readingTime: Math.ceil(countWords(post.content.rendered) / 200),
    seo: { yoastHead: post.yoast_head, yoastHeadJSON: post.yoast_head_json },
    author: { 
        id: authorData?.id || post.author, 
        name: authorData?.name || "Oolook Team", 
        avatarUrl: makeAbsolute(authorData?.avatar_urls?.['96']),
        description: authorData?.description || ''
    },
    featuredMedia: { id: post.featured_media, source_url: makeAbsolute(featuredMediaData?.source_url) },
    categories,
    tags
  };
}


// ----------- BLOGS -----------

export const fetchBlogPosts = async ({ page = 1, perPage = 10, authorId = null } = {}) => {
  // Base URL construction
  let url = `${WP_API_BASE_URL}/wp-json/wp/v2/posts?_embed&per_page=${perPage}&page=${page}`;
  
  // Add author parameter if provided for server-side filtering
  if (authorId) {
    url += `&author=${authorId}`;
  }
  
  // Add cache buster
  url += `&t=${new Date().getTime()}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`API error fetching posts: ${response.status}`);
  }

  // Get total pages and posts from the response headers
  const totalPages = parseInt(response.headers.get('X-WP-TotalPages'), 10) || 1;
  const totalPosts = parseInt(response.headers.get('X-WP-Total'), 10) || 0;

  const rawPosts = await response.json();
  const posts = rawPosts.map(transformBlogPost);

  return { posts, totalPages, totalPosts };
};

export const fetchBlogPostBySlug = async (slug) => {
  const cacheBuster = `&t=${new Date().getTime()}`;
  const url = `${WP_API_BASE_URL}/wp-json/wp/v2/posts?slug=${slug}&_embed${cacheBuster}`;
  
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`API error fetching post by slug: ${response.status}`);
  }
  
  const rawPosts = await response.json();
  
  if (rawPosts && rawPosts.length > 0) {
    return transformBlogPost(rawPosts[0]); 
  }
  
  return null;
};

export const fetchAllAuthors = async () => {
  const url = `${WP_API_BASE_URL}/wp-json/wp/v2/users`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`API error fetching authors: ${response.status}`);
  
  const rawAuthors = await response.json();

  // Transform the raw author data to fix avatar URLs
  return rawAuthors.map(author => {
    const wpBaseUrl = 'https://blogs.oolook.in';
    const makeAbsolute = (url) => {
      if (!url || url.startsWith('http')) return url;
      const path = url.startsWith('/') ? url : `/${url}`;
      return `${wpBaseUrl}${path}`;
    };

    return {
      ...author, // Keep all original author data
      // Overwrite the avatar_urls object with absolute paths
      avatar_urls: {
        '24': makeAbsolute(author.avatar_urls['24']),
        '48': makeAbsolute(author.avatar_urls['48']),
        '96': makeAbsolute(author.avatar_urls['96']),
      },
    };
  });
};


// ----------- FORMS -----------

// ✅ GRACEFUL ERROR HANDLING: Both functions now attach the status code to the error object.
const handleFormError = async (response) => {
  const errorData = await response.json().catch(() => ({}));
  const errorMessage = errorData.detail || errorData.message || `Request failed with status ${response.status}`;
  const error = new Error(errorMessage);
  error.status = response.status; // Attach status code to the error
  throw error;
};

export async function submitEmail(email) {
const url = `${LEAD_API_BASE_URL}/common/capture-lead`;
const res = await fetch(url, {
  method : "POST",
  headers: { "Content-Type": "application/json" },
  body   : JSON.stringify({ email }),
});
if (!res.ok) {
  await handleFormError(res);
}
return await res.json();
}

export const submitDemoRequest = async (formData) => {
const response = await fetch(`${LEAD_API_BASE_URL}/common/capture-lead`, {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
  },
  body: JSON.stringify(formData),
});

if (!response.ok) {
  await handleFormError(response);
}
return await response.json();
};

// ----------- MINI-TOOLS -----------

export async function fetchOzoneData(url, maxPages = 15) {
  const payload = { url, max_pages: maxPages };
  const res = await fetch(`${API_BASE_URL}/crawl`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${process.env.REACT_APP_API_TOKEN}` },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
  const json = await res.json();
  return Object.entries(json.results).map(([u, info]) => ({ url: u, phones: info.contact_numbers || [], emails: info.emails || [], social: info.social_links || [] }));
}

export const removeBackgroundFromUrl = async (imageUrl) => {
  const response = await fetch(`${API_BASE_URL}/remove_bg`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ image_url: imageUrl }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to remove background via URL");
  }
  return await response.blob();
};

export const removeBackgroundFromFile = async (file) => {
  const formData = new FormData();
  formData.append("image", file);
  const response = await fetch(`${API_BASE_URL}/remove_bg`, { method: "POST", body: formData });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to remove background via file");
  }
  return await response.blob();
};

export const removeVideoBgFromFile = async (file) => {
  const formData = new FormData();
  formData.append("video", file);
  const response = await fetch(`${API_BASE_URL}/remove_video_bg`, { method: "POST", body: formData });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to remove video background via file");
  }
  return await response.blob();
};

export const removeVideoBgFromUrl = async (videoUrl) => {
  const response = await fetch(`${API_BASE_URL}/remove_video_bg`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ video_url: videoUrl }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to remove video background via URL");
  }
  return await response.blob();
};

export const convertFile = async (file, category, outputFormat) => {
  const formData = new FormData();
  formData.append("category", category);
  formData.append("output_format", outputFormat);
  formData.append("file", file);
  const response = await fetch(`${API_BASE_URL}/convert`, { method: "POST", body: formData });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Conversion failed");
  }
  return await response.blob();
};

export async function generateCaptions(options) {
  const { url, file, prompt = "", platform = "instagram", tone = "uplifting", brandPreset = "AcmeCorp", emojiDensity = "none", captionLength = "long", generateHashtags = true, keywords = [], n = 3 } = options;

  if (file && !url) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("prompt", prompt);
    formData.append("platform", platform);
    formData.append("tone", tone);
    formData.append("brand_preset", brandPreset);
    formData.append("emoji_density", emojiDensity);
    formData.append("caption_length", captionLength);
    formData.append("generate_hashtags", generateHashtags);
    formData.append("n", String(n));
    if (keywords.length > 0) formData.append("keywords", keywords.join(","));
    const resp = await fetch(`${API_BASE_URL}/caption`, { method: "POST", body: formData });
    if (!resp.ok) throw new Error(`Caption generation failed: ${await resp.text()}`);
    return await resp.json();
  } else {
    const payload = { prompt, platform, tone, brand_preset: brandPreset, emoji_density: emojiDensity, caption_length: captionLength, generate_hashtags: generateHashtags, n, keywords };
    if (url) payload.url = url;
    const resp = await fetch(`${API_BASE_URL}/caption`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (!resp.ok) throw new Error(`Caption generation failed: ${await resp.text()}`);
    return await resp.json();
  }
}

export async function expandCaption(originalText) {
  const payload = { caption: originalText, expand: true, generate_similar: false, n: 1 };
  const resp = await fetch(`${API_BASE_URL}/caption-variants`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
  if (!resp.ok) throw new Error(`Expand failed: ${await resp.text()}`);
  return await resp.json();
}

export async function rewriteCaption(originalText) {
  const payload = { caption: originalText, expand: false, generate_similar: true, n: 3 };
  const resp = await fetch(`${API_BASE_URL}/caption-variants`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
  if (!resp.ok) throw new Error(`Rewrite failed: ${await resp.text()}`);
  return await resp.json();
}


export const fetchPricingPlans = async () => {
  // Using LEAD_API_BASE_URL as it's the common backend service
  const url = `${LEAD_API_BASE_URL}/common/plans`;
  const response = await fetch(url);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.detail || errorData.message || "Failed to fetch pricing plans";
    throw new Error(errorMessage);
  }
  const data = await response.json();
  return data.plans; // The API returns { success: true, plans: [...] }
};
